/**
 * DirectiveStatusHost — чипы хода отправки директивы создателя.
 *
 * Смонтирован ОДИН раз в AdminPanel (App.tsx) и рисует состояние модульного
 * стора directive-dispatch: «Падла пишет…» (сразу после закрытия диалога),
 * «Отправлено» (по факту ответа сервера, самоуходит), ошибка (висит с
 * «Повторить», текст директивы не теряется — заново открытый диалог того же
 * чата предзаполнится им).
 *
 * Самодостаточные тёмные чипы (без завязки на палитру страницы): хост живёт и
 * поверх NerveCenter, и поверх Viewer. z-index выше модала директивы (10005),
 * чтобы чип был виден и при повторно открытом диалоге.
 */

import * as React from 'react';
import ReactDOM from 'react-dom';
import {
  dismissDispatch,
  getDispatches,
  retryDispatch,
  subscribeDispatches,
  type DirectiveDispatch,
} from '../lib/directive-dispatch';

function ElapsedSeconds({ since }: { since: number }): React.ReactElement {
  const [, tick] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.max(0, Math.round((Date.now() - since) / 1000));
  return <span style={{ opacity: 0.6, fontVariantNumeric: 'tabular-nums' }}>{secs} с</span>;
}

function chipLabel(d: DirectiveDispatch): string {
  const who = d.displayName || String(d.chatId);
  if (d.status === 'sending') {
    const how = d.voice ? 'записывает голосовое' : 'пишет сообщение';
    return d.withTools ? `Падла ${how} (с инструментами) — ${who}` : `Падла ${how} — ${who}`;
  }
  if (d.status === 'sent') {
    return d.voice ? `Отправлено голосом — ${who}` : `Отправлено — ${who}`;
  }
  return `Не отправлено — ${who}`;
}

const CHIP_BASE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  maxWidth: 380,
  padding: '10px 14px',
  borderRadius: 12,
  background: 'rgba(24,24,27,0.96)',
  border: '1px solid rgba(255,255,255,0.12)',
  boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
  color: '#fafafa',
  fontSize: 13,
  lineHeight: 1.4,
};

function Chip({ d }: { d: DirectiveDispatch }): React.ReactElement {
  const accent = d.status === 'error' ? '#f87171' : d.status === 'sent' ? '#4ade80' : '#a5b4fc';
  return (
    <div style={{ ...CHIP_BASE, borderColor: d.status === 'error' ? 'rgba(248,113,113,0.4)' : CHIP_BASE.border as string }}>
      <span aria-hidden style={{ color: accent, marginTop: 1 }}>
        {d.status === 'sending' ? (
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: accent,
              animation: 'padla-directive-pulse 1.2s ease-in-out infinite',
            }}
          />
        ) : d.status === 'sent' ? (
          '✓'
        ) : (
          '⚠'
        )}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 600, display: 'flex', gap: 8, alignItems: 'baseline' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {chipLabel(d)}
          </span>
          {d.status === 'sending' && <ElapsedSeconds since={d.startedAt} />}
        </div>
        {d.note && (
          <div style={{ color: 'rgba(250,250,250,0.65)', fontSize: 12, marginTop: 2 }}>{d.note}</div>
        )}
        {d.status === 'error' && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              onClick={() => retryDispatch(d.id)}
              style={{
                fontSize: 12,
                padding: '4px 10px',
                borderRadius: 8,
                background: 'rgba(248,113,113,0.15)',
                border: '1px solid rgba(248,113,113,0.4)',
                color: '#fca5a5',
              }}
            >
              Повторить
            </button>
            <button
              onClick={() => dismissDispatch(d.id)}
              style={{
                fontSize: 12,
                padding: '4px 10px',
                borderRadius: 8,
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'rgba(250,250,250,0.6)',
              }}
            >
              Закрыть
            </button>
          </div>
        )}
      </div>
      {d.status !== 'error' && (
        <button
          onClick={() => dismissDispatch(d.id)}
          aria-label="Скрыть"
          style={{ color: 'rgba(250,250,250,0.4)', fontSize: 14, lineHeight: 1, marginTop: 1 }}
        >
          ×
        </button>
      )}
    </div>
  );
}

export default function DirectiveStatusHost(): React.ReactElement | null {
  const dispatches = React.useSyncExternalStore(subscribeDispatches, getDispatches, getDispatches);
  if (dispatches.length === 0) return null;
  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 10010,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        pointerEvents: 'auto',
      }}
    >
      <style>{`@keyframes padla-directive-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.35; transform: scale(0.75); } }`}</style>
      {dispatches.map(d => (
        <Chip key={d.id} d={d} />
      ))}
    </div>,
    document.body,
  );
}
